import { useState, useEffect, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Landmark, Loader2 } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/contexts/AuthContext'
import { useOrg } from '@/contexts/OrgContext'
import { invokeWithFreshAuth } from '@/lib/edge-functions'
import { toast } from 'sonner'

// Banks BC form — CREATE (no editTarget) and EDIT (editTarget set). The AE picks
// which optional levers apply via toggles; only an enabled lever's inputs are
// sent (and only an enabled lever renders a slide). Inputs map 1:1 with BanksInputs
// in supabase/functions/_shared/banks-bc-math.ts.

export interface BanksEditTarget {
  slug: string
  clientName: string
  country: string | null
  language: 'es' | 'en' | 'pt'
  deckDate: string | null
  inputs: Record<string, unknown>
}

interface Props {
  open: boolean
  onClose: () => void
  onSaved?: (slug: string, url: string, total: number) => void
  editTarget?: BanksEditTarget | null
}

type LeverKey = 'token' | 'taptopay' | 'pos' | 'threeds' | 'impl'

export function NewBanksBcForm({ open, onClose, onSaved, editTarget }: Props) {
  const { user } = useAuth()
  const { org } = useOrg()
  const qc = useQueryClient()
  const isEdit = !!editTarget

  // Identity
  const [createdByEmail, setCreatedByEmail] = useState('')
  const [clientName, setClientName] = useState('')
  const [country, setCountry] = useState('MX')
  const [language, setLanguage] = useState<'es' | 'en' | 'pt'>('es')
  const [currency, setCurrency] = useState('USD')
  const [deckDate, setDeckDate] = useState('2026')

  // Base (hard-required) + lever a
  const [monthlyTx, setMonthlyTx] = useState('')
  const [avgTicket, setAvgTicket] = useState('')
  const [currentAr, setCurrentAr] = useState('')
  const [targetAr, setTargetAr] = useState('')
  const [takeRate, setTakeRate] = useState('15')

  // Lever toggles
  const [levers, setLevers] = useState<Record<LeverKey, boolean>>({
    token: false, taptopay: false, pos: false, threeds: false, impl: false,
  })
  const toggle = (k: LeverKey) => setLevers((p) => ({ ...p, [k]: !p[k] }))

  // (b) token
  const [pctRecurring, setPctRecurring] = useState('')
  const [deltaArToken, setDeltaArToken] = useState('')
  const [recurringBase, setRecurringBase] = useState('')
  // (c) tap to pay
  const [physTx, setPhysTx] = useState('')
  const [ticketFisico, setTicketFisico] = useState('')
  const [takeFisico, setTakeFisico] = useState('')
  // (d) pos routing
  const [tpvPresencial, setTpvPresencial] = useState('')
  const [acquirers, setAcquirers] = useState('') // "Name:1.8, Name:1.6"
  const [blendedMdr, setBlendedMdr] = useState('')
  const [routedMdr, setRoutedMdr] = useState('')
  // (e) 3ds
  const [tpv3ds, setTpv3ds] = useState('')
  const [deltaAr3ds, setDeltaAr3ds] = useState('')
  const [cartAbandon, setCartAbandon] = useState('')
  // (f) impl
  const [backlog, setBacklog] = useState('')
  const [costPerIntegration, setCostPerIntegration] = useState('')
  const [yunoUnified, setYunoUnified] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (user?.email && !createdByEmail) setCreatedByEmail(user.email)
  }, [user, createdByEmail])

  // Hydrate on open — edit prefill, or reset to blank defaults for create.
  // The form stays mounted in Presentaciones: without the reset branch, the
  // previous edit's values leak into "Nuevo Banks BC" (wrong client's data).
  useEffect(() => {
    if (!open) return
    if (editTarget) {
      const i = editTarget.inputs || {}
      const s = (v: unknown) => (v == null ? '' : String(v))
      setClientName(editTarget.clientName || '')
      setCountry(editTarget.country || 'MX')
      // El deck público (src/banks-bc/) solo renderiza español — forzar 'es'
      // incluso si la fila guardó en/pt antes del fix, para que al guardar se
      // normalice y el badge de idioma deje de mentir (BUG-053).
      setLanguage('es')
      setDeckDate(editTarget.deckDate || '2026')
      setCurrency(s(i.currency) || 'USD')
      setMonthlyTx(s(i.monthly_transactions)); setAvgTicket(s(i.avg_ticket_usd))
      setCurrentAr(s(i.current_approval_rate_pct)); setTargetAr(s(i.target_approval_rate_pct))
      setTakeRate(s(i.take_rate_pct) || '15')
      setPctRecurring(s(i.pct_recurring_onfile)); setDeltaArToken(s(i.delta_ar_token_pp)); setRecurringBase(s(i.recurring_approval_rate_pct))
      setPhysTx(s(i.est_physical_tx_monthly)); setTicketFisico(s(i.avg_ticket_fisico_usd)); setTakeFisico(s(i.take_fisico_pct))
      setTpvPresencial(s(i.tpv_presencial_annual_usd)); setBlendedMdr(s(i.current_blended_mdr_pct)); setRoutedMdr(s(i.routed_mdr_pct))
      if (Array.isArray(i.pos_acquirers)) {
        setAcquirers((i.pos_acquirers as Array<{ name?: string; mdr_pct?: number }>).map((a) => `${a.name || ''}:${a.mdr_pct ?? ''}`).join(', '))
      } else {
        setAcquirers('')
      }
      setTpv3ds(s(i.tpv_3ds_annual_usd)); setDeltaAr3ds(s(i.delta_ar_3ds_pp)); setCartAbandon(s(i.cart_abandon_delta_pp))
      setBacklog(s(i.integrations_backlog)); setCostPerIntegration(s(i.cost_per_integration_usd)); setYunoUnified(s(i.yuno_unified_cost_usd))
      const en = Array.isArray(i.levers_enabled) ? (i.levers_enabled as string[]) : null
      if (en) {
        setLevers({
          token: en.includes('token'), taptopay: en.includes('taptopay'), pos: en.includes('pos'),
          threeds: en.includes('threeds'), impl: en.includes('impl'),
        })
      } else {
        // Skill back-compat: rows without levers_enabled activate a lever by
        // input presence (mirrors banks-bc-math's isEnabled default). Without
        // this, saving an edit would send levers_enabled=[] and silently turn
        // off every lever the deck had.
        const on = (v: unknown) => Number(v) > 0
        setLevers({
          token: on(i.pct_recurring_onfile) && on(i.delta_ar_token_pp),
          taptopay: on(i.est_physical_tx_monthly) && on(i.avg_ticket_fisico_usd) && on(i.take_fisico_pct),
          pos: Array.isArray(i.pos_acquirers) && (i.pos_acquirers as unknown[]).length >= 2 && on(i.tpv_presencial_annual_usd),
          threeds: on(i.tpv_3ds_annual_usd) && on(i.delta_ar_3ds_pp),
          impl: on(i.integrations_backlog) && on(i.cost_per_integration_usd),
        })
      }
    } else {
      setClientName('')
      setCountry('MX')
      setLanguage('es')
      setDeckDate('2026')
      setCurrency('USD')
      setMonthlyTx(''); setAvgTicket('')
      setCurrentAr(''); setTargetAr('')
      setTakeRate('15')
      setPctRecurring(''); setDeltaArToken(''); setRecurringBase('')
      setPhysTx(''); setTicketFisico(''); setTakeFisico('')
      setTpvPresencial(''); setBlendedMdr(''); setRoutedMdr('')
      setAcquirers('')
      setTpv3ds(''); setDeltaAr3ds(''); setCartAbandon('')
      setBacklog(''); setCostPerIntegration(''); setYunoUnified('')
      setLevers({ token: false, taptopay: false, pos: false, threeds: false, impl: false })
    }
    setError('')
  }, [open, editTarget])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (!clientName.trim()) { setError('Nombre del banco requerido'); return }
    if (!isEdit && !createdByEmail.trim()) { setError('Gmail conectado es requerido para crear'); return }
    if (!monthlyTx.trim() || !avgTicket.trim() || !currentAr.trim()) {
      setError('tx/mes, ticket y approval actual son obligatorios'); return
    }

    // Blank → explicit null (undefined keys drop out of the JSON body and the
    // update endpoint would keep the stored value; null tells it to clear it).
    const num = (v: string) => (v.trim() === '' ? null : Number(v))
    const inputs: Record<string, unknown> = {
      monthly_transactions: num(monthlyTx),
      avg_ticket_usd: num(avgTicket),
      current_approval_rate_pct: num(currentAr),
      target_approval_rate_pct: num(targetAr),
      take_rate_pct: num(takeRate),
      currency,
    }
    const enabled = (Object.keys(levers) as LeverKey[]).filter((k) => levers[k])
    inputs.levers_enabled = enabled

    if (levers.token) {
      inputs.pct_recurring_onfile = num(pctRecurring)
      inputs.delta_ar_token_pp = num(deltaArToken)
      inputs.recurring_approval_rate_pct = num(recurringBase)
    }
    if (levers.taptopay) {
      inputs.est_physical_tx_monthly = num(physTx)
      inputs.avg_ticket_fisico_usd = num(ticketFisico)
      inputs.take_fisico_pct = num(takeFisico)
    }
    if (levers.pos) {
      inputs.tpv_presencial_annual_usd = num(tpvPresencial)
      inputs.current_blended_mdr_pct = num(blendedMdr)
      inputs.routed_mdr_pct = num(routedMdr)
      const list = acquirers.split(',').map((s) => s.trim()).filter(Boolean).map((pair) => {
        const [name, mdr] = pair.split(':').map((x) => x.trim())
        return { name: name || 'Adquirente', mdr_pct: Number(mdr) }
      }).filter((a) => Number.isFinite(a.mdr_pct))
      inputs.pos_acquirers = list.length ? list : null
    }
    if (levers.threeds) {
      inputs.tpv_3ds_annual_usd = num(tpv3ds)
      inputs.delta_ar_3ds_pp = num(deltaAr3ds)
      inputs.cart_abandon_delta_pp = num(cartAbandon)
    }
    if (levers.impl) {
      inputs.integrations_backlog = num(backlog)
      inputs.cost_per_integration_usd = num(costPerIntegration)
      inputs.yuno_unified_cost_usd = num(yunoUnified)
    }

    setSubmitting(true)
    try {
      const endpoint = isEdit ? 'banks-bc-update' : 'banks-bc-generate'
      const body: Record<string, unknown> = isEdit
        ? { slug: editTarget!.slug, client_name: clientName.trim(), country, language, currency, deck_date: deckDate.trim() || null, inputs }
        : { createdByEmail: createdByEmail.trim(), org_id: org?.id, client_name: clientName.trim(), country, language, currency, deck_date: deckDate.trim() || null, inputs }

      const { data, error: invokeErr } = await invokeWithFreshAuth<{ slug?: string; url?: string; warnings?: Record<string, boolean>; business_case?: { total_annual_value: number } }>(endpoint, { body, timeoutMs: 60_000 })
      if (invokeErr) {
        const ctx = (invokeErr as { context?: Response }).context
        if (ctx && typeof ctx.json === 'function') {
          try {
            const b = await ctx.clone().json() as { error?: string } | null
            if (b?.error) throw new Error(b.error)
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message && parseErr.message !== 'Unexpected end of JSON input') throw parseErr
          }
        }
        throw invokeErr
      }

      const result = data as { slug?: string; url?: string; warnings?: Record<string, boolean>; business_case?: { total_annual_value: number } } | null
      if (!result?.slug && !isEdit) throw new Error('Respuesta inválida del servidor (sin slug)')
      const slug = result?.slug || editTarget?.slug || ''
      const url = result?.url || `https://chief.yuno.tools/banks/${slug}`
      const total = result?.business_case?.total_annual_value ?? 0
      const warns = Object.entries(result?.warnings || {}).filter(([, v]) => v).map(([k]) => k)

      qc.invalidateQueries({ queryKey: ['banks_bc'] })
      toast.success(isEdit ? `Banks BC actualizado · ${clientName.trim()}` : `Banks BC creado · ${clientName.trim()}`, {
        description: `Total: ${total.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}/año${warns.length ? ` · ⚠ ${warns.join(', ')}` : ''} · ${url}`,
        action: { label: 'Abrir', onClick: () => window.open(url, '_blank', 'noopener') },
      })
      onSaved?.(slug, url, total)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error generando el Banks BC')
    } finally {
      setSubmitting(false)
    }
  }

  const leverMeta: Array<{ key: LeverKey; label: string; hint: string }> = [
    { key: 'token', label: 'Vaulted token / Tokenización', hint: 'Network tokens sobre on-file' },
    { key: 'taptopay', label: 'Apple Tap to Pay', hint: 'Nuevo stream físico' },
    { key: 'pos', label: 'POS multi-adquirente', hint: 'Ruteo MDR (≥2 adquirentes)' },
    { key: 'threeds', label: 'Nuevos proveedores 3DS', hint: '↑AR sin fricción' },
    { key: 'impl', label: 'Implementación (one-time)', hint: 'Σ integraciones − Yuno' },
  ]

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose() }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5" /> {isEdit ? 'Editar Banks BC' : 'Nuevo Banks BC'}</DialogTitle>
          <DialogDescription>Caso de negocio para banco / institución financiera. La palanca a (reintentos) es la base; activá las demás según apliquen al cliente.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cliente */}
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground">Cliente</h4>
            {!isEdit && (
              <div className="space-y-1">
                <Label htmlFor="b-email">Gmail conectado (AE)</Label>
                <Input id="b-email" type="email" value={createdByEmail} onChange={(e) => setCreatedByEmail(e.target.value)} placeholder="ae@y.uno" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="b-name">Banco *</Label>
                <Input id="b-name" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Banorte" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="b-country">País (ISO-2)</Label>
                <Input id="b-country" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} placeholder="MX" maxLength={2} />
              </div>
              <div className="space-y-1">
                <Label>Idioma</Label>
                {/* Banks deck solo existe en español — en/pt deshabilitados hasta portar BanksSlides a i18n (BUG-053) */}
                <Select value={language} onValueChange={(v) => setLanguage(v as 'es' | 'en' | 'pt')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en" disabled>English (próximamente)</SelectItem>
                    <SelectItem value="pt" disabled>Português (próximamente)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Moneda (display)</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['USD', 'MXN', 'BRL', 'COP', 'ARS', 'CLP', 'PEN', 'EUR', 'GBP'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="b-date">Fecha</Label>
                <Input id="b-date" value={deckDate} onChange={(e) => setDeckDate(e.target.value)} placeholder="2026" />
              </div>
            </div>
          </section>

          {/* Volumen + palanca a */}
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground">Volumen + Reintentos (palanca a · base)</h4>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Tx aprobadas / mes *" value={monthlyTx} set={setMonthlyTx} placeholder="4000000" />
              <Field label="Ticket promedio (USD) *" value={avgTicket} set={setAvgTicket} placeholder="60" />
              <Field label="Approval actual % *" value={currentAr} set={setCurrentAr} placeholder="69" />
              <Field label="Approval objetivo %" value={targetAr} set={setTargetAr} placeholder="73" />
              <Field label="Margin / take %" value={takeRate} set={setTakeRate} placeholder="15" />
            </div>
          </section>

          {/* Lever toggles */}
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground">Palancas a incluir</h4>
            <div className="space-y-3 rounded-lg border p-3">
              {leverMeta.map((l) => (
                <div key={l.key}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{l.label}</div>
                      <div className="text-xs text-muted-foreground">{l.hint}</div>
                    </div>
                    <Switch checked={levers[l.key]} onCheckedChange={() => toggle(l.key)} />
                  </div>
                  {levers[l.key] && (
                    <div className="mt-2 grid grid-cols-3 gap-2 border-l-2 border-muted pl-3">
                      {l.key === 'token' && <>
                        <Field label="% recurrente on-file" value={pctRecurring} set={setPctRecurring} placeholder="25" />
                        <Field label="ΔAR token (pp)" value={deltaArToken} set={setDeltaArToken} placeholder="7" />
                        <Field label="Base AR recurrente %" value={recurringBase} set={setRecurringBase} placeholder="70" />
                      </>}
                      {l.key === 'taptopay' && <>
                        <Field label="Tx físicas / mes" value={physTx} set={setPhysTx} placeholder="150000" />
                        <Field label="Ticket físico (USD)" value={ticketFisico} set={setTicketFisico} placeholder="40" />
                        <Field label="Take físico %" value={takeFisico} set={setTakeFisico} placeholder="1.5" />
                      </>}
                      {l.key === 'pos' && <>
                        <Field label="TPV presencial anual (USD)" value={tpvPresencial} set={setTpvPresencial} placeholder="500000000" />
                        <Field label="MDR blended actual %" value={blendedMdr} set={setBlendedMdr} placeholder="1.8" />
                        <Field label="MDR ruteado %" value={routedMdr} set={setRoutedMdr} placeholder="1.5" />
                        <div className="col-span-3 space-y-1">
                          <Label className="text-xs">Adquirentes (Nombre:MDR%, separados por coma · ≥2)</Label>
                          <Input value={acquirers} onChange={(e) => setAcquirers(e.target.value)} placeholder="Adq A:1.8, Adq B:1.6, Adq C:1.5" />
                        </div>
                      </>}
                      {l.key === 'threeds' && <>
                        <Field label="TPV 3DS anual (USD)" value={tpv3ds} set={setTpv3ds} placeholder="800000000" />
                        <Field label="ΔAR 3DS (pp)" value={deltaAr3ds} set={setDeltaAr3ds} placeholder="3" />
                        <Field label="Δ cart abandon (pp)" value={cartAbandon} set={setCartAbandon} placeholder="0" />
                      </>}
                      {l.key === 'impl' && <>
                        <Field label="Backlog de integraciones" value={backlog} set={setBacklog} placeholder="6" />
                        <Field label="Costo por integración (USD)" value={costPerIntegration} set={setCostPerIntegration} placeholder="250000" />
                        <Field label="Costo unificado Yuno (USD)" value={yunoUnified} set={setYunoUnified} placeholder="250000" />
                      </>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {error && <div className="text-sm text-red-500">{error}</div>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Generar deck'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, value, set, placeholder }: { label: string; value: string; set: (v: string) => void; placeholder?: string }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => set(e.target.value)} placeholder={placeholder} inputMode="decimal" />
    </div>
  )
}
